import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Search, ClipboardList, Shield, TrendingUp, FileText, CheckCircle, User, Download } from 'lucide-react';

export default function DocumentosPage() {
  const [pacientes, setPacientes] = useState([]);
  const [pacienteSelecionado, setPacienteSelecionado] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPacientes, setFilteredPacientes] = useState([]);
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [avaliacaoId, setAvaliacaoId] = useState('');

  useEffect(() => {
    const buscarPacientes = async () => {
      try {
        const response = await api.get('/patients');
        setPacientes(response.data.data);
      } catch (err) {
        toast.error("Falha ao carregar a lista de pacientes.");
      }
    };
    buscarPacientes();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredPacientes([]);
    } else {
      const results = pacientes.filter(p =>
        p.nome.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPacientes(results);
    }
  }, [searchTerm, pacientes]);

  const handlePatientSelect = async (patient) => {
    setSearchTerm('');
    setFilteredPacientes([]);
    setPacienteSelecionado(patient);
    setAvaliacaoId('');
    try {
      const res = await api.get(`/avaliacoes/paciente/${patient._id}`);
      setAvaliacoes(res.data.data || []);
    } catch {
      setAvaliacoes([]);
    }
  };

  const downloadBlob = (data, filename) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleGenerateEvaluation = async () => {
    if (!pacienteSelecionado) return;
    const toastId = toast.loading('Gerando Ficha de Avaliação...');
    try {
      const params = avaliacaoId ? `?avaliacaoId=${avaliacaoId}` : '';
      const response = await api.get(`/patients/${pacienteSelecionado._id}/evaluation-form${params}`, { responseType: 'blob' });
      downloadBlob(response.data, `avaliacao_${pacienteSelecionado.nome.replace(/\s+/g, '_')}.pdf`);
      toast.success('Ficha de Avaliação gerada!', { id: toastId });
    } catch (error) {
      toast.error('Não foi possível gerar a ficha.', { id: toastId });
    }
  };

  const handleGenerateConsent = async () => {
    if (!pacienteSelecionado) return;
    const toastId = toast.loading('Gerando Termo de Consentimento...');
    try {
      const response = await api.get(`/patients/${pacienteSelecionado._id}/consent-term`, { responseType: 'blob' });
      downloadBlob(response.data, `termo_consentimento_${pacienteSelecionado.nome.replace(/\s+/g, '_')}.pdf`);
      toast.success('Termo de Consentimento gerado!', { id: toastId });
    } catch (error) {
      toast.error('Não foi possível gerar o termo.', { id: toastId });
    }
  };

  const handleGenerateDischarge = async () => {
    if (!pacienteSelecionado) return;
    const toastId = toast.loading('Gerando Declaração de Alta...');
    try {
      const response = await api.get(`/patients/${pacienteSelecionado._id}/discharge-summary`, { responseType: 'blob' });
      downloadBlob(response.data, `declaracao_alta_${pacienteSelecionado.nome.replace(/\s+/g, '_')}.pdf`);
      toast.success('Declaração de Alta gerada!', { id: toastId });
    } catch (error) {
      toast.error('Não foi possível gerar a declaração.', { id: toastId });
    }
  };

  const handleGenerateReferral = async () => {
    if (!pacienteSelecionado) return;
    const toastId = toast.loading('Gerando Relatório de Encaminhamento...');
    try {
      const response = await api.get(`/patients/${pacienteSelecionado._id}/referral-report`, { responseType: 'blob' });
      downloadBlob(response.data, `encaminhamento_${pacienteSelecionado.nome.replace(/\s+/g, '_')}.pdf`);
      toast.success('Relatório de Encaminhamento gerado!', { id: toastId });
    } catch (error) {
      toast.error('Não foi possível gerar o relatório.', { id: toastId });
    }
  };

  const handleGenerateAttendance = async () => {
    if (!pacienteSelecionado) return;
    const toastId = toast.loading('Gerando Atestado de Comparecimento...');
    try {
      const response = await api.get(`/patients/${pacienteSelecionado._id}/attendance-certificate`, { responseType: 'blob' });
      downloadBlob(response.data, `atestado_comparecimento_${pacienteSelecionado.nome.replace(/\s+/g, '_')}.pdf`);
      toast.success('Atestado gerado!', { id: toastId });
    } catch (error) {
      toast.error('Não foi possível gerar o atestado.', { id: toastId });
    }
  };

  const formatDateBR = (isoString) => {
    if (!isoString) return '';
    const part = String(isoString).split('T')[0];
    const [y, m, d] = part.split('-');
    return `${d}/${m}/${y}`;
  };

  const documentos = [
    {
      titulo: 'Ficha de Avaliação',
      descricao: 'Gere a ficha de avaliação fisioterapêutica do paciente.',
      icone: <ClipboardList className="w-7 h-7 text-blue-600" />,
      iconeBg: 'bg-blue-50',
      botaoCor: 'text-blue-600 border-blue-200 hover:bg-blue-50',
      acao: handleGenerateEvaluation,
    },
    {
      titulo: 'Termo de Consentimento',
      descricao: 'Gere o termo de consentimento livre e esclarecido (TCLE).',
      icone: <Shield className="w-7 h-7 text-green-600" />,
      iconeBg: 'bg-green-50',
      botaoCor: 'text-green-600 border-green-200 hover:bg-green-50',
      acao: handleGenerateConsent,
    },
    {
      titulo: 'Declaração de Alta',
      descricao: 'Gere a declaração de alta fisioterapêutica do paciente.',
      icone: <TrendingUp className="w-7 h-7 text-purple-600" />,
      iconeBg: 'bg-purple-50',
      botaoCor: 'text-purple-600 border-purple-200 hover:bg-purple-50',
      acao: handleGenerateDischarge,
    },
    {
      titulo: 'Relatório de Encaminhamento',
      descricao: 'Gere o relatório de encaminhamento para outros profissionais.',
      icone: <FileText className="w-7 h-7 text-orange-600" />,
      iconeBg: 'bg-orange-50',
      botaoCor: 'text-orange-600 border-orange-200 hover:bg-orange-50',
      acao: handleGenerateReferral,
    },
    {
      titulo: 'Atestado de Comparecimento',
      descricao: 'Gere o atestado de comparecimento para o paciente.',
      icone: <CheckCircle className="w-7 h-7 text-teal-600" />,
      iconeBg: 'bg-teal-50',
      botaoCor: 'text-teal-600 border-teal-200 hover:bg-teal-50',
      acao: handleGenerateAttendance,
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">

      {/* Cabeçalho */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Gerenciamento de Documentos</h1>
        <p className="text-gray-500 mt-1">Selecione um paciente e gere documentos de forma rápida e segura.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Coluna principal */}
        <div className="lg:col-span-2 space-y-6">

          {/* Card selecionar paciente */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-base font-semibold text-gray-700 mb-4">
              1. Selecione o Paciente
            </h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Digite para buscar um paciente..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
                value={pacienteSelecionado ? pacienteSelecionado.nome : searchTerm}
                onChange={(e) => {
                  if (pacienteSelecionado) setPacienteSelecionado(null);
                  setSearchTerm(e.target.value);
                }}
              />
              {searchTerm.trim() !== '' && (
                <ul className="absolute z-10 w-full bg-white border border-gray-200 mt-1 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                  {filteredPacientes.length > 0 ? (
                    filteredPacientes.map(p => (
                      <li
                        key={p._id}
                        className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3 text-sm"
                        onClick={() => handlePatientSelect(p)}
                      >
                        <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center">
                          <User className="w-3.5 h-3.5 text-blue-600" />
                        </div>
                        <span className="font-medium text-gray-900">{p.nome}</span>
                      </li>
                    ))
                  ) : (
                    <li className="px-4 py-3 text-gray-500 text-sm">Nenhum paciente encontrado.</li>
                  )}
                </ul>
              )}
            </div>

            {/* Paciente selecionado */}
            {pacienteSelecionado && (
              <div className="mt-3 flex items-center gap-3 p-3 bg-blue-50 rounded-xl border border-blue-100">
                <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
                  <User className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{pacienteSelecionado.nome}</p>
                  <p className="text-xs text-gray-500">{pacienteSelecionado.diagnosisTitle || 'Sem diagnóstico'}</p>
                </div>
                <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                  Selecionado
                </span>
              </div>
            )}

            {/* Seletor de avaliação — usado pela Ficha de Avaliação */}
            {pacienteSelecionado && (
              <div className="mt-4">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Avaliação para a Ficha de Avaliação
                </label>
                {avaliacoes.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">
                    Nenhuma avaliação registrada para este paciente. O PDF será gerado sem dados de avaliação.
                  </p>
                ) : (
                  <select
                    value={avaliacaoId}
                    onChange={e => setAvaliacaoId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
                  >
                    <option value="">Mais recente ({avaliacoes[0] && formatDateBR(avaliacoes[0].dataAvaliacao)})</option>
                    {avaliacoes.map(av => (
                      <option key={av._id} value={av._id}>
                        {formatDateBR(av.dataAvaliacao)} — {av.queixaPrincipal?.slice(0, 50) || 'Sem queixa'}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>

          {/* Card tipos de documento */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-base font-semibold text-gray-700 mb-4">
              2. Escolha o Tipo de Documento
            </h2>

            {!pacienteSelecionado ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <FileText className="w-7 h-7 text-gray-400" />
                </div>
                <p className="text-sm text-gray-500">Selecione um paciente acima</p>
                <p className="text-xs text-gray-400 mt-1">para liberar os documentos disponíveis</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {documentos.map((doc, index) => (
                  <div
                    key={index}
                    className="border border-gray-100 rounded-xl p-4 flex flex-col gap-3 hover:border-gray-200 hover:shadow-sm transition-all"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-12 h-12 rounded-xl ${doc.iconeBg} flex items-center justify-center flex-shrink-0`}>
                        {doc.icone}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{doc.titulo}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{doc.descricao}</p>
                      </div>
                    </div>
                    <button
                      onClick={doc.acao}
                      className={`w-full flex items-center justify-center gap-2 border rounded-lg py-2 text-sm font-medium transition-colors ${doc.botaoCor}`}
                    >
                      <Download className="w-4 h-4" />
                      Gerar Documento
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Coluna lateral — dica */}
        <div className="space-y-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Documentos disponíveis</h3>
            </div>
            <ul className="space-y-2">
              {[
                { nome: 'Ficha de Avaliação', cor: 'bg-blue-500' },
                { nome: 'Termo de Consentimento', cor: 'bg-green-500' },
                { nome: 'Declaração de Alta', cor: 'bg-purple-500' },
                { nome: 'Rel. de Encaminhamento', cor: 'bg-orange-500' },
                { nome: 'Atestado de Comparecimento', cor: 'bg-teal-500' },
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                  <span className={`w-2 h-2 rounded-full ${item.cor} flex-shrink-0`}></span>
                  {item.nome}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-blue-50 rounded-2xl border border-blue-100 p-5">
            <p className="text-sm font-semibold text-blue-800 mb-1">💡 Dica</p>
            <p className="text-xs text-blue-700 leading-relaxed">
              Todos os documentos são gerados em PDF e baixados automaticamente. Selecione o paciente antes de gerar qualquer documento.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}